import { FrontendControl } from '@app/data-stores/ptr/ptr-schemas';
import type {
  ProductTypeValue,
  PTRDataStoreBlankItem,
  PTRDataStoreType,
} from '@app/data-stores/ptr/types';
import { DeepReadonly } from '@app/types';
import type { InventoryItem } from '@app/types/shop-bff/types';
import { useViewportWidth } from '@redbubble/next/hooks';
import { DeviceType } from '@redbubble/next/types';
import { useCallback, useMemo, useState } from 'react';
import { DSFormChangeType } from '../components/ProductConfigurator/types';
import {
  ConfiguratorOption,
  FieldConfigurationOption,
  PRODUCT_FAMILY_OPTION,
  PRODUCT_TYPE_OPTION,
  sortOptions,
} from '../components/PTRProductConfigurator/utils';
import {
  canonicaliseSelectedOptions,
  canonicaliseTweakedSelectedOptions,
  FAMILY_OPTION_PREFIX,
  findProductFamilyById,
  findProductTypeByFamilyOptionSelector,
  optionsCanonicalHash,
  PRODUCT_OPTION_PREFIX,
  prefixedOptionsCanonicalHash,
} from '../utils/ptrConfiguratorUtils';

export type InventoryBySelector = Record<string, Record<string, InventoryItem>>;

export type CurrentConfiguratorValues = {
  productOptionsValues: Record<string, string>;
  familyOptionsValues: Record<string, string>;
  prefixedProductOptionsValues: Record<string, string>;
  prefixedFamilyOptionsValues: Record<string, string>;
};

export interface UsePTRProductConfiguratorParams {
  currentInventoryItem: InventoryItem;
  setCurrentInventoryItem: (inventoryItem: InventoryItem) => void;
  productTypes: PTRDataStoreType['productTypes'];
  productFamilies: PTRDataStoreType['productFamilies'];
  blankItemsById: Record<string, PTRDataStoreBlankItem>;
  initialProductType: DeepReadonly<ProductTypeValue>;
  inventoryBySelector: InventoryBySelector;
}

export interface UsePTRProductConfiguratorReturn {
  currentProductType: DeepReadonly<ProductTypeValue>;
  currentProductFamily: PTRDataStoreType['productFamilies'][number];
  validOptionValues: Record<string, Set<string>>;
  inventoryBySelector: InventoryBySelector;
  currentValues: CurrentConfiguratorValues;
  updateForm: (
    formState: DSFormChangeType,
    prevState: DSFormChangeType,
    excludeFromAnalytics?: boolean
  ) => void;
  showingFallback: boolean;
  showColorConfigSeparately: boolean;
  colorConfiguratorOptions: ConfiguratorOption[];
  configuratorOptions: ConfiguratorOption[];
}

export const usePTRProductConfigurator = ({
  currentInventoryItem,
  setCurrentInventoryItem,
  productTypes,
  productFamilies,
  blankItemsById,
  initialProductType,
  inventoryBySelector,
}: UsePTRProductConfiguratorParams): UsePTRProductConfiguratorReturn => {
  const productFamilyId =
    initialProductType.productFamilyId ?? initialProductType.productTypeId;

  const currentProductFamily = useMemo(
    () => findProductFamilyById(productTypes, productFamilyId, productFamilies),
    [productFamilies, productTypes, productFamilyId]
  );

  const currentProductType = initialProductType;

  const [productTypeEquivalentCanonical, setProductTypeEquivalentCanonical] =
    useState<string | undefined>(() => {
      const productType = productTypes[currentProductType.productTypeId];
      return productType.productFamilyOptionSelections?.equivalentCanonical;
    });

  const productType = useMemo(() => {
    return (
      findProductTypeByFamilyOptionSelector(
        productTypes,
        productFamilyId,
        productTypeEquivalentCanonical
      ) ?? productTypes[currentProductFamily.defaultProductTypeId]
    );
  }, [
    productTypes,
    productFamilyId,
    productTypeEquivalentCanonical,
    currentProductFamily,
  ]);

  const [blankItemEquivalentCanonical, setBlankItemEquivalentCanonical] =
    useState<string>(() => {
      const blankItem = blankItemsById[currentInventoryItem.blankItemId];
      return blankItem?.options?.equivalentCanonical;
    });

  const { blankItem, isFallback: showingFallback } = useMemo(() => {
    const invItem =
      inventoryBySelector[productType?.productTypeId]?.[
        blankItemEquivalentCanonical
      ];
    if (invItem) {
      return {
        blankItem: blankItemsById[invItem.blankItemId],
        isFallback: false,
      };
    }
    return {
      blankItem: blankItemsById[productType?.defaultBlankItemId],
      isFallback: true,
    };
  }, [
    inventoryBySelector,
    productType,
    blankItemEquivalentCanonical,
    blankItemsById,
  ]);

  const validOptionValues = useMemo(() => {
    const index: Record<string, Set<string>> = {};
    for (const items of Object.values(inventoryBySelector)) {
      for (const item of Object.values(items)) {
        if (!item) continue;

        const blankItem = blankItemsById[item.blankItemId];
        if (!blankItem) continue;

        const productType = Object.values(productTypes).find(
          (p) => p.productTypeId === item.productTypeId
        );
        if (!productType) continue;

        for (const productTypeOptionValue of blankItem?.options?.values || []) {
          const key = `${PRODUCT_TYPE_OPTION}:${productTypeOptionValue.key}`;
          index[key] ||= new Set([]);
          index[key].add(productTypeOptionValue.value);
        }

        for (const productFamilyOptionValue of productType
          ?.productFamilyOptionSelections?.values || []) {
          const key = `${PRODUCT_FAMILY_OPTION}:${productFamilyOptionValue.key}`;
          index[key] ||= new Set([]);
          index[key].add(productFamilyOptionValue.value);
        }
      }
    }
    return index;
  }, [blankItemsById, inventoryBySelector, productTypes]);

  const prefixedFamilyOptionsValues = useMemo(() => {
    return prefixedOptionsCanonicalHash(
      productType?.productFamilyOptionSelections?.equivalentCanonical,
      PRODUCT_FAMILY_OPTION
    );
  }, [productType]);
  const prefixedProductOptionsValues = useMemo(
    () =>
      prefixedOptionsCanonicalHash(
        blankItem?.options?.equivalentCanonical,
        PRODUCT_TYPE_OPTION
      ),
    [blankItem]
  );

  const familyOptionsValues = useMemo(() => {
    return optionsCanonicalHash(
      productType?.productFamilyOptionSelections?.equivalentCanonical
    );
  }, [productType]);
  const productOptionsValues = useMemo(
    () => optionsCanonicalHash(blankItem?.options?.equivalentCanonical),
    [blankItem]
  );

  const currentValues = useMemo(
    () => ({
      productOptionsValues,
      prefixedProductOptionsValues,
      familyOptionsValues,
      prefixedFamilyOptionsValues,
    }),
    [
      productOptionsValues,
      prefixedProductOptionsValues,
      familyOptionsValues,
      prefixedFamilyOptionsValues,
    ]
  );

  const familyOptionKeys = useMemo(
    () =>
      new Set(
        currentProductFamily?.options?.map(
          (o) => `${FAMILY_OPTION_PREFIX}${o.key}`
        ) ?? []
      ),
    [currentProductFamily]
  );

  const handleProductTypeSelectorChange = (selector: string) => {
    const newProductType = findProductTypeByFamilyOptionSelector(
      productTypes,
      productFamilyId,
      selector
    );
    if (!newProductType) return;

    let newItem =
      inventoryBySelector[newProductType.productTypeId]?.[
        blankItemEquivalentCanonical
      ];

    if (!newItem) {
      const blankItem = blankItemsById[newProductType.defaultBlankItemId];
      newItem =
        inventoryBySelector[newProductType.productTypeId]?.[
          blankItem.options.equivalentCanonical
        ];
    }

    setProductTypeEquivalentCanonical(selector);
    setCurrentInventoryItem(newItem);
  };

  const handleBlankItemSelectorChange = (selector: string) => {
    const newItem = inventoryBySelector[productType?.productTypeId]?.[selector];
    if (newItem) {
      setBlankItemEquivalentCanonical(selector);
      setCurrentInventoryItem(newItem);
    }
  };

  const updateForm = (
    formState: DSFormChangeType,
    prevFormState: DSFormChangeType,
    excludeFromAnalytics?: boolean
  ) => {
    const changedKey = Object.keys(formState.values).find(
      (key) => formState.values[key] !== prevFormState.values[key]
    );

    if (!changedKey) return;

    const newValue = formState.values[changedKey];

    const currentValue =
      currentValues.prefixedProductOptionsValues[changedKey] ??
      currentValues.prefixedFamilyOptionsValues[changedKey];
    // On cases where we fall back to rendering the default blank item, formik reinitialises with the new key, this
    // causes the onChange function to execute with a different value than what's in state.
    // Therefor we need to check for this and return early bypassing this update, so we keep the current item and
    // productType/blankItem state in sync.
    if (newValue === currentValue) return;

    if (familyOptionKeys.has(changedKey)) {
      const newFamilyValues = {
        ...familyOptionsValues,
        [changedKey.replace(FAMILY_OPTION_PREFIX, '')]: newValue,
      };
      delete newFamilyValues[changedKey];
      const newSelector = canonicaliseSelectedOptions(newFamilyValues);
      handleProductTypeSelectorChange(newSelector);
    } else {
      const newProductValues = {
        ...productOptionsValues,
        [changedKey.replace(PRODUCT_OPTION_PREFIX, '')]: newValue,
      };
      delete newProductValues[changedKey];
      const newSelector = canonicaliseSelectedOptions(newProductValues);
      handleBlankItemSelectorChange(newSelector);
    }
  };

  const { isOrAbove } = useViewportWidth();
  const isMobile = !isOrAbove(DeviceType.Tablet);
  const getProductTypeFilteredValues = useCallback(
    ({
      option,
      validOptionValues,
    }: {
      option: FieldConfigurationOption;
      validOptionValues: Record<string, Set<string>>;
    }) => {
      const ignoreItem = option.entity === PRODUCT_FAMILY_OPTION;

      return option.values
        .map((value) => {
          const isValidValue = validOptionValues?.[
            `${option.entity}:${option.key}`
          ]?.has(value.value);
          if (!isValidValue) return null;

          const tweakedCanonical = canonicaliseTweakedSelectedOptions(
            currentValues.productOptionsValues,
            option.key,
            value.equivalentValue
          );
          const inventoryItem =
            inventoryBySelector[currentProductType.productTypeId][
              tweakedCanonical
            ];

          if (!inventoryItem && !ignoreItem) return null;
          return { value, inventoryItem };
        })
        .filter((value): value is NonNullable<typeof value> => value !== null);
    },
    [currentValues, inventoryBySelector, currentProductType]
  );

  const configuratorOptions = useMemo(() => {
    const sortedOptions = sortOptions(productType, currentProductFamily);
    return sortedOptions.map((option) => {
      return {
        ...option,
        key:
          option.entity === PRODUCT_FAMILY_OPTION
            ? `${FAMILY_OPTION_PREFIX}${option.key}`
            : `${PRODUCT_OPTION_PREFIX}${option.key}`,
        filterValues: getProductTypeFilteredValues({
          option,
          validOptionValues,
        }),
      };
    });
  }, [
    getProductTypeFilteredValues,
    validOptionValues,
    productType,
    currentProductFamily,
  ]);

  const colorConfiguratorOptions = configuratorOptions.filter(
    (option) => option.frontendControl === FrontendControl.Color
  );
  const showColorConfigSeparately =
    isMobile && colorConfiguratorOptions.length > 0;

  return {
    configuratorOptions,
    currentProductType,
    currentProductFamily,
    validOptionValues,
    inventoryBySelector,
    currentValues,
    updateForm,
    showColorConfigSeparately,
    colorConfiguratorOptions,
    showingFallback,
  };
};
